import { breakpoints } from '@app/utils/breakpoints';
import {
    Drawer,
    ListItemIcon,
    Menu,
    MenuItem,
    type MenuItemProps,
    MenuList,
    Typography,
    useMediaQuery,
} from '@mui/material';
import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

//#region : Responsive Menu Item component

/** Custom. */
export type ResponsiveMenuItemProps = {
    text: string;
    textProps?: {
        className?: string;
    };
    icon?: React.ReactElement;
    iconProps?: {
        className?: string;
    };
} & MenuItemProps;

/** Custom menu item. Anything below 'sm' is for pull-up menu and >= 'sm' is for simple menu. */
export function ResponsiveMenuItem({ text, textProps, icon, iconProps, className, ...rest }: ResponsiveMenuItemProps) {
    const isMobile = useMediaQuery(breakpoints.downSm, {
        noSsr: true,
    });

    return (
        <MenuItem
            className={twMerge(
                clsx('text-gray-t0', {
                    'px-[12px] py-[10px] first:pt-0 last:pb-0': !isMobile,
                }),
                className
            )}
            {...rest}
        >
            {isMobile && icon && (
                <ListItemIcon
                    className={twMerge('mr-[15px] !min-w-0 justify-start text-inherit', iconProps?.className)}
                >
                    {icon}
                </ListItemIcon>
            )}
            <span
                className={twMerge(
                    clsx('font-semibold', {
                        'text-[15px] leading-[20.43px]': isMobile,
                        'text-[14px] leading-tight': !isMobile,
                    }),
                    textProps?.className
                )}
            >
                {text}
            </span>
        </MenuItem>
    );
}
//#endregion : Responsive Menu Item component

//#region : Responsive Menu component

//#region : Main component parameter types
type PullUpMenuProps = {
    className?: string;
    heading?: string;
    paperProps?: {
        className?: string;
    };
};

type SimpleMenuProps = {
    className?: string;
    paperProps?: {
        className?: string;
    };
};

export type ResponsiveMenuProps = {
    MenuOpenerComponent: React.ReactElement;
    hideOnBreakPoint?: string;
    children?: React.ReactNode;
} & (
    | {
          menuType: 'simple';
          simpleMenuProps?: SimpleMenuProps;
          pullUpMenuProps?: never;
      }
    | {
          menuType: 'pull-up';
          simpleMenuProps?: never;
          pullUpMenuProps?: PullUpMenuProps;
      }
    | {
          menuType: 'hybrid';
          simpleMenuProps?: SimpleMenuProps;
          pullUpMenuProps?: PullUpMenuProps;
      }
);
//#endregion : Main component parameter types

/** Custom inline responsive menu. Shows pull-up menu for resolutions below 'sm' and >= 'sm' is for simple menu. */
export function ResponsiveMenu({
    MenuOpenerComponent,
    menuType,
    hideOnBreakPoint,
    children,
    simpleMenuProps,
    pullUpMenuProps,
}: ResponsiveMenuProps) {
    const isMobile = useMediaQuery(breakpoints.downSm, {
        noSsr: true,
    });

    let hideMenu = useMediaQuery(hideOnBreakPoint ? hideOnBreakPoint : '', {
        noSsr: true,
    });

    hideMenu = hideOnBreakPoint ? hideMenu : false;

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const [menuOpen, setMenuOpen] = useState(false);

    const handleMenuOpen = useCallback(() => {
        setMenuOpen(true);
    }, []);

    const handleMenuClose = useCallback(() => {
        setMenuOpen(false);
    }, []);

    const OpenerComponent = React.cloneElement(MenuOpenerComponent, {
        onClick: handleMenuOpen,
        ref: setAnchorEl,
        'aria-haspopup': 'true',
        'aria-expanded': menuOpen ? 'true' : undefined,
    });

    return (
        <React.Fragment>
            {OpenerComponent}
            {!hideMenu && (
                <>
                    {isMobile && (menuType === 'pull-up' || menuType === 'hybrid') && (
                        <Drawer
                            open={menuOpen}
                            onClose={handleMenuClose}
                            anchor="bottom"
                            PaperProps={{
                                style: {
                                    maxHeight: '434px',
                                    marginTop: '15px',
                                    overflowY: 'auto',
                                },
                                className: twMerge(
                                    'dark:bg-[#333333] dark:bg-none',
                                    pullUpMenuProps?.paperProps?.className
                                ),
                            }}
                            className={pullUpMenuProps?.className}
                        >
                            <MenuList className="pb-[20px]">
                                {pullUpMenuProps?.heading && (
                                    <MenuItem className="pt-[13px] pb-[29px]" disableRipple>
                                        <Typography fontWeight="bold">{pullUpMenuProps?.heading}</Typography>
                                    </MenuItem>
                                )}
                                {children}
                                <ResponsiveMenuItem onClick={handleMenuClose} text="Cancel" />
                            </MenuList>
                        </Drawer>
                    )}

                    {!isMobile && (menuType === 'simple' || menuType === 'hybrid') && (
                        <Menu
                            anchorEl={anchorEl}
                            open={menuOpen}
                            onClose={handleMenuClose}
                            PaperProps={{
                                className: twMerge(
                                    'rounded-[8px] py-[14px] px-[0px] dark:bg-[#313131] min-w-[160px]',
                                    simpleMenuProps?.paperProps?.className
                                ),
                            }}
                            MenuListProps={{
                                className: 'p-0',
                            }}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            className={simpleMenuProps?.className}
                        >
                            {children}
                        </Menu>
                    )}
                </>
            )}
        </React.Fragment>
    );
}
//#endregion : Main component
